import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ActivityIcon, MapIcon, LayersIcon } from 'lucide-react';
const steps = [{
  phase: '01',
  icon: 'activity',
  title: 'Meten',
  subtitle: 'Negentig dagen advertentiedata',
  desc: 'We zetten een gerichte advertentiecampagne op, niet als doel maar als meetinstrument. Per zoekopdracht registreren we klikken, aanvragen en kosten per aanvraag. Wekelijks sturen we bij: zoekopdrachten zonder resultaat gaan uit, kansrijke breiden we uit. Zo wordt de meting steeds scherper.',
  tag: 'Dag 1 tot 90',
  color: '#0066FF'
}, {
  phase: '02',
  icon: 'map',
  title: 'Rapporteren',
  subtitle: 'Het CorePrint-rapport',
  desc: 'Je krijgt een marktanalyse met de zoekopdrachten die bewezen aanvragen opleveren, de zoekopdrachten die volume hadden maar niets deden, en de kosten per aanvraag per term. Plus een bouwplan met prioriteiten. Het rapport en de onderliggende data zijn jouw eigendom.',
  tag: 'Week 12',
  color: '#0052CC'
}, {
  phase: '03',
  icon: 'layers',
  title: 'Bouwen',
  subtitle: 'Structuur op bewezen vraag',
  desc: 'Op basis van het rapport bouwen we de organische structuur op, pagina voor pagina, alleen op termen waarvan is aangetoond dat ze converteren. Ingericht om ook vindbaar te zijn in AI-gedreven zoekmachines. Naarmate de posities stijgen, kan het advertentiebudget omlaag.',
  tag: 'Maand 4 en verder',
  color: '#28C840'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.23, 1, 0.32, 1]
    }
  }
};
export function Process() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="hoe" ref={ref} className="py-24 relative overflow-hidden" style={{
    background: 'linear-gradient(160deg, #0D1628 0%, #1B2B44 100%)'
  }} data-id="element-320" data-mp="2qdmjfg9cq8u9">
      <div className="absolute inset-0 grid-bg opacity-30" data-id="element-321" data-mp="2v65ti38tylub" />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6" data-id="element-322" data-mp="2hehki8tvodhk">
        <motion.div className="max-w-3xl mb-16" initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} data-id="element-323" data-mp="2mb806v1rt72g">
          <div className="section-label-dark" data-id="element-324" data-mp="352inz3c793q4">Het traject</div>
          <h2 className="text-[clamp(2rem,3.6vw,2.8rem)] font-bold text-white leading-tight tracking-tight mb-4" data-id="element-325" data-mp="350xulh1mfqtu">
            Hoe CorePrint werkt
          </h2>
          <p className="text-white/60 text-lg leading-relaxed" data-id="element-326" data-mp="1tnveso7cj5aq">
            Drie fases, met een vast moment waarop je beslist of je doorgaat. Na
            fase twee ligt het rapport er en kun je zelf bepalen wat je met de
            uitkomst doet.
          </p>
        </motion.div>

        <div className="relative" data-id="element-327" data-mp="1v494ha43k0bk">
          {/* Verbindingslijn tussen de fases */}
          <motion.div className="absolute hidden lg:block" style={{
          top: 72,
          left: '6%',
          right: '6%',
          height: 1,
          background: 'linear-gradient(90deg, transparent 0%, rgba(0,102,255,0.5) 15%, rgba(0,102,255,0.7) 50%, rgba(0,102,255,0.5) 85%, transparent 100%)'
        }} initial={{
          scaleX: 0,
          originX: 0
        }} animate={inView ? {
          scaleX: 1
        } : {}} transition={{
          duration: 1.2,
          delay: 0.3,
          ease: 'easeOut'
        }} data-id="element-328" data-mp="1q5epykd5wxx9" />

          <motion.div className="grid lg:grid-cols-3 gap-6 items-stretch" variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-329" data-mp="sejfru4ymzlp">
            {steps.map(step => <motion.div key={step.phase} className="card-dark p-9 flex flex-col" variants={itemVariants} data-id="element-330" data-mp="172e8vbmvr5zb">
                {/* Fasenummer en icoon */}
                <div className="flex items-center gap-5 mb-7" data-id="element-331" data-mp="1g1j4oa724l7s">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{
                background: `${step.color}18`,
                border: `1px solid ${step.color}40`
              }} data-id="element-332" data-mp="1l1nl607ytfy0">
                    {step.icon === 'activity' && <ActivityIcon size={24} style={{
                  color: step.color
                }} data-id="element-333" data-mp="3rsn0kqydmpz6" />}
                    {step.icon === 'map' && <MapIcon size={24} style={{
                  color: step.color
                }} data-id="element-334" data-mp="203vpteqsm31s" />}
                    {step.icon === 'layers' && <LayersIcon size={24} style={{
                  color: step.color
                }} data-id="element-335" data-mp="2nu9mjikboq2p" />}
                  </div>
                  <div data-id="element-336" data-mp="2dv0nibalol9h">
                    <div className="text-[0.7rem] font-bold tracking-widest uppercase" style={{
                  color: step.color
                }} data-id="element-337" data-mp="2dvtvtgp7jitq">
                      Fase {step.phase}
                    </div>
                    <div className="text-white/40 text-xs mt-1 font-medium" data-id="element-338" data-mp="13yvghoegpp6t">
                      {step.tag}
                    </div>
                  </div>
                </div>

                <h3 className="font-bold text-white text-xl mb-2" data-id="element-339" data-mp="uwm5yyj71vtf">
                  {step.title}
                </h3>
                <div className="text-[0.85rem] font-semibold mb-5" style={{
              color: step.color
            }} data-id="element-340" data-mp="zunptlog1z5y">
                  {step.subtitle}
                </div>
                <p className="text-white/60 text-[0.95rem] leading-relaxed" data-id="element-341" data-mp="c761k04e1j7q">
                  {step.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}